import React, { useMemo } from 'react';
import {
	Flex,
	Grid,
	Typography,
	Table as AntdTable,
} from 'antd';
import { Link } from 'react-router-dom';
import { formatCurrency } from '@shared/utils/currency';
import {
	FixtureTypes,
	PnlBunkerTypes,
} from '@shared/utils/constants';
import { formatQuantity } from '@shared/utils/string';
import { standardSort } from '@shared/utils/standardSort';
import type { PnlBunker } from '@api/utils/bunker-expenditure/getTcOutPnlBunkers';
import { useVoyage } from '@client/screens/fleet/VoyageDetailsScreen/components/VoyageProvider/VoyageProvider';
import Table, { AlignType } from '@client/components/Table/Table';
import { Links } from '@client/utils/links';
import { formatDate } from '@client/utils/formatDate';
import { ValidBunkerModalTypes } from '@client/utils/hooks/useBunkerModal';
import Modal from '@client/components/Modal/Modal';

type Props = {
	open: boolean;
	close: () => void;
	type: ValidBunkerModalTypes;
};

const BunkerGainLossModal = ({
	open,
	close,
	type,
}: Props) => {
	const screens = Grid.useBreakpoint();
	const isTcIn = type === FixtureTypes.TC_IN;

	const {
		voyageDetails,
		tcPnl,
		vcPnl,
		fixtureCurrency,
	} = useVoyage();

	const columns = useMemo(() => {
		const renderDetails = (value: PnlBunker['details']) => {
			const text = value?.text;
			let dateStr = '[no date]';

			if (value?.date) {
				dateStr = formatDate(value.date) ?? '[no date]';
			}

			const finalText = `${text} ${dateStr} for`;

			return (
				<Flex gap={3}>
					<Typography.Text>{finalText}</Typography.Text>
					<Link to={Links.Voyage.get(value?.contractId!)}>{value?.contractIdentifier ?? 'Unnamed'}</Link>
				</Flex>
			);
		};

		return [
			{
				title: 'Item',
				dataIndex: 'item',
				sorter: standardSort('item'),
			},
			{
				title: 'Details',
				dataIndex: 'details',
				render: renderDetails,
				sorter: standardSort(['details', 'text']),
			},
			{
				title: 'Grade',
				dataIndex: 'fuelGrade',
				sorter: standardSort('fuelGrade'),
			},
			{
				title: 'Quantity',
				dataIndex: 'quantity',
				render: (value: number) => formatQuantity(value, 'MT'),
				align: 'right' as AlignType,
			},
			{
				title: 'Purchase Price',
				dataIndex: 'purchasePrice',
				render: (value: number, row: any) => {
					if (row.type === PnlBunkerTypes.PARCEL && row.currency) {
						return formatCurrency(value, row.currency);
					}

					return (<em>-</em>);
				},
				align: 'right' as AlignType,
			},
			{
				title: isTcIn ? 'Redelivery Price' : 'Delivery Price',
				dataIndex: 'deliveryPrice',
				render: (value: number, row: any) => {
					if (row.type === PnlBunkerTypes.DELIVERY && row.currency) {
						return formatCurrency(value, row.currency);
					}

					return (<em>-</em>);
				},
				align: 'right' as AlignType,
			},
			{
				title: 'Value',
				dataIndex: 'value',
				render: (value: number, row: any) => {
					if (row.currency) {
						return formatCurrency(value, row.currency);
					}

					return (<em>-</em>);
				},
				align: 'right' as AlignType,
			},
		];
	}, [isTcIn]);

	const tableData = useMemo(() => {
		if (isTcIn) {
			return voyageDetails.tciPnlBunkers ?? [];
		}

		return voyageDetails.tcoPnlBunkers ?? [];
	}, [voyageDetails, isTcIn]);

	const getTitle = () => {
		if (isTcIn) {
			return 'TC In Redelivery Bunker Gain / Loss';
		}

		return 'TC Out Delivery Bunker Gain / Loss';
	};

	const total = useMemo(() => {
		if (voyageDetails.fixture.type === FixtureTypes.SPOT) {
			if (!isTcIn) {
				return 0;
			}

			return vcPnl?.tcInRedeliveryBunkerGainLoss.actual ?? 0;
		}

		if (isTcIn) {
			return tcPnl?.tcInRedeliveryBunkerGainLoss.actual ?? 0;
		}

		return tcPnl?.tcOutDeliveryBunkerGainLoss.actual ?? 0;
	}, [tcPnl, vcPnl, isTcIn, voyageDetails]);

	return (
		<Modal
			title={getTitle()}
			open={open}
			onCancel={close}
			okText="Close"
			destroyOnClose
			width={`calc(100vw - ${screens.lg ? '15%' : '5%'})`}
			okButtonProps={{
				onClick: close,
			}}
			cancelButtonProps={{
				style: {
					visibility: 'hidden',
				},
			}}
		>
			<Table
				data-testid="gain-loss-table"
				columns={columns}
				dataSource={tableData}
				pagination={false}
				size="small"
				summary={() => (
					<AntdTable.Summary.Row>
						<AntdTable.Summary.Cell index={0}>
							<b>Total Gain / Loss</b>
						</AntdTable.Summary.Cell>
						<AntdTable.Summary.Cell index={1} />
						<AntdTable.Summary.Cell index={2} />
						<AntdTable.Summary.Cell index={3} />
						<AntdTable.Summary.Cell index={4} />
						<AntdTable.Summary.Cell index={5} />
						<AntdTable.Summary.Cell index={6} align="right">
							<b>
								{formatCurrency(
									total,
									fixtureCurrency,
								)}
							</b>
						</AntdTable.Summary.Cell>
					</AntdTable.Summary.Row>
				)}
			/>
		</Modal>
	);
};

export default BunkerGainLossModal;
